import { DateTime } from "luxon";
import { useParams } from "react-router-dom";
import "../../style/scss/Resume1.scss";
import { useResumePresentationQuery } from "../../query/useResumePresentation";

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return undefined;
}

function ContactBlock({ person, location }) {
  const firstName = pick(person, "firstName", "FirstName") || "";
  const lastName = pick(person, "lastName", "LastName") || "";
  return (
    <>
      <div className="Resume_NameTitle">
        {firstName}&nbsp;{lastName}
      </div>
      {location ? (
        <div className="Resume_ContactInfo">
          <div className="Resume_Address">
            {pick(location, "address1", "Address1")},
            {pick(location, "cityName", "CityName")},
            {pick(location, "stateName", "StateName")},
            {pick(location, "postalCode", "PostalCode")}
          </div>
        </div>
      ) : (
        "n/a"
      )}
    </>
  );
}

function EmploymentBlock({ jobs }) {
  if (!jobs?.length) return null;
  return (
    <div className="Resume_Employments">
      <div className="SectionHeader">Employment History</div>
      <ul className="Resume_Employment">
        {jobs.map((e) => {
          const id = pick(e, "id", "Id", "ID");
          const start = pick(e, "startDate", "StartDate");
          const end = pick(e, "endDate", "EndDate");
          return (
            <li key={id}>
              {pick(e, "companyName", "CompanyName") || "Employer"} -
              <span className="Resume_JobTitle">{pick(e, "title", "Title")}</span>
              <span className="Resume_JobDates">
                ({start ? DateTime.fromISO(start).toFormat("MM/yyyy") : "N/A"} -{" "}
                {end ? DateTime.fromISO(end).toFormat("MM/yyyy") : "Now"})
              </span>
              <div className="Resume_JobDescription">
                {pick(e, "shortDescription", "ShortDescription")}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function EducationBlock({ education }) {
  if (!education?.length) return null;
  return (
    <div className="Resume_Skills">
      <div className="SectionHeader">Education</div>
      <ul>
        {education.map((e) => (
          <li key={pick(e, "id", "Id", "ID")}>
            <span className="Resume_Education_Degree">{pick(e, "degree", "Degree")}</span> -
            <span className="Resume_Education_Years">
              {pick(e, "yearStart", "YearStart") || "N/A"}-
              {pick(e, "yearEnd", "YearEnd") || "now"}
            </span>
            <span className="Resume_Education_Institution">
              {pick(e, "institution", "Institution")}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CertBlock({ certifications }) {
  if (!certifications?.length) return null;
  return (
    <div className="Resume_Skills">
      <div className="SectionHeader">Certifications</div>
      <ul>
        {certifications.map((c) => {
          const received = pick(c, "dateReceived", "DateReceived");
          return (
            <li key={pick(c, "id", "Id", "ID")}>
              {pick(c, "name", "Name")} -{" "}
              {received ? DateTime.fromISO(received).toFormat("MM/yyyy") : "unknown"}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ObjectiveBlock({ objective }) {
  return (
    <div className="Resume_Objective">
      Objective: {objective || "n/a"}
    </div>
  );
}

function SkillsBlock({ skills, title = "Skills", withMonths = false }) {
  if (!skills?.length) return null;
  return (
    <div className="Resume_Skills">
      <div className="SectionHeader">{title}</div>
      <ul>
        {skills.map((s) => {
          const months = pick(s, "experienceMonths", "ExperienceMonths") ?? 0;
          const name = pick(s, "skillName", "SkillName");
          const key = pick(s, "skillId", "SkillId", "ID") || name;
          return (
            <li key={key}>
              {withMonths ? (
                <>
                  <strong>{name}</strong>
                  {months > 0 ? ` — ${months} months` : ""}
                </>
              ) : (
                name
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ResumeShell({ testId, formatKey, children }) {
  return (
    <div className={`resume${formatKey === "chronological" ? " resumeChronological" : ""}${formatKey === "skillsFirst" ? " resumeSkillsFirst" : ""}`} data-testid={testId} data-resume-format={formatKey}>
      {children}
    </div>
  );
}

function ResumeBody({ data }) {
  const formatKey = data.formatKey || "classic";
  const person = data.person;
  const location = data.location;
  const objective = data.objective;
  const skills = data.skills;
  const jobs = data.jobs;
  const education = data.education;
  const certifications = data.certifications;

  if (formatKey === "chronological" || data.format === 2) {
    return (
      <ResumeShell testId="resume-chronological" formatKey="chronological">
        <ContactBlock person={person} location={location} />
        <ObjectiveBlock objective={objective} />
        <EmploymentBlock jobs={jobs} />
        <SkillsBlock skills={skills} />
        <EducationBlock education={education} />
        <CertBlock certifications={certifications} />
      </ResumeShell>
    );
  }

  if (formatKey === "skillsFirst" || data.format === 3) {
    return (
      <ResumeShell testId="resume-skills-first" formatKey="skillsFirst">
        <ContactBlock person={person} location={location} />
        <ObjectiveBlock objective={objective} />
        <SkillsBlock skills={skills} title="Core skills" withMonths />
        <EmploymentBlock jobs={jobs} />
        <EducationBlock education={education} />
        <CertBlock certifications={certifications} />
      </ResumeShell>
    );
  }

  return (
    <ResumeShell testId="resume-classic" formatKey="classic">
      <ContactBlock person={person} location={location} />
      <ObjectiveBlock objective={objective} />
      <SkillsBlock skills={skills} />
      <EmploymentBlock jobs={jobs} />
      <EducationBlock education={education} />
      <CertBlock certifications={certifications} />
    </ResumeShell>
  );
}

export default function ResumeView() {
  const { id: idStr } = useParams();
  const careerId = parseInt(idStr, 10);
  const { data, isLoading, isError, error } = useResumePresentationQuery(careerId);

  if (!(careerId > 0)) {
    return <p className="fieldHint">Invalid resume link.</p>;
  }
  if (isLoading) {
    return <p className="fieldHint">Loading resume…</p>;
  }
  if (isError) {
    return (
      <p className="fieldHint">
        {error?.code === "not_found" ? "Resume not found." : error?.message || "Could not load resume."}
      </p>
    );
  }
  if (!data) {
    return <p className="fieldHint">Resume not found.</p>;
  }

  return <ResumeBody data={data} />;
}

/** Kept for any direct imports; prefer ResumeView. */
export function ResumeClassic({ id }) {
  const { data, isLoading, isError, error } = useResumePresentationQuery(id);
  if (isLoading) return <p className="fieldHint">Loading resume…</p>;
  if (isError || !data) return <p className="fieldHint">{error?.message || "Could not load resume."}</p>;
  return <ResumeBody data={{ ...data, format: 1, formatKey: "classic" }} />;
}

export function ResumeChronological({ id }) {
  const { data, isLoading, isError, error } = useResumePresentationQuery(id);
  if (isLoading) return <p className="fieldHint">Loading resume…</p>;
  if (isError || !data) return <p className="fieldHint">{error?.message || "Could not load resume."}</p>;
  return <ResumeBody data={{ ...data, format: 2, formatKey: "chronological" }} />;
}

export function ResumeSkillsFirst({ id }) {
  const { data, isLoading, isError, error } = useResumePresentationQuery(id);
  if (isLoading) return <p className="fieldHint">Loading resume…</p>;
  if (isError || !data) return <p className="fieldHint">{error?.message || "Could not load resume."}</p>;
  return <ResumeBody data={{ ...data, format: 3, formatKey: "skillsFirst" }} />;
}
